import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  PiXLight,
  PiQuestionLight,
  PiCaretDownLight,
  PiCaretRightLight,
  PiBriefcaseLight,
  PiCheckSquareOffsetLight,
  PiUsersLight,
  PiMagnifyingGlassLight,
  PiFileTextLight,
  PiArticleLight,
  PiDatabaseLight,
  PiTableLight,
  PiChatsCircleLight,
  PiChartBarLight,
  PiLightningLight,
  PiShieldCheckLight,
  PiTrendUpLight,
  PiPaperPlaneRightLight,
  PiChatDotsLight,
  PiBuildingsLight,
  PiUserLight,
} from "react-icons/pi";
import apis from "../../utils/apis";
import Button from "../shared/TailwindComponents/Button";
import Textarea from "../shared/TailwindComponents/Textarea";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AnalyticsHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  isOrgAdmin: boolean;
}

interface HelpSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  metrics: HelpMetric[];
}

interface HelpMetric {
  name: string;
  explanation: string;
  howCalculated: string;
  whyItMatters: string;
  type: "card" | "chart" | "gauge";
}

/* ------------------------------------------------------------------ */
/*  Help Content                                                       */
/* ------------------------------------------------------------------ */

const getPersonalSections = (t: TFunction): HelpSection[] => [
  {
    id: "matters",
    title: t("Matters"),
    icon: <PiBriefcaseLight className="w-4 h-4" />,
    description: t("Everything related to your legal matters - cases, disputes, transactions, and other work items you manage."),
    metrics: [
      {
        name: t("Total matters"),
        explanation: t("The number of matters (cases) you created in the selected time period."),
        howCalculated: t("Counts all matters you opened between the start and end dates."),
        whyItMatters: t("Helps you understand your workload volume over time."),
        type: "card",
      },
      {
        name: t("Matters over time"),
        explanation: t("A timeline showing how many new matters you opened each day, week, or month."),
        howCalculated: t("Groups your created matters into time buckets (days, weeks, or months) so you can spot trends."),
        whyItMatters: t("Reveals busy periods and helps with planning ahead."),
        type: "chart",
      },
      {
        name: t("Status breakdown"),
        explanation: t("A pie chart showing how your matters are distributed across stages like Open, In Progress, Closed, etc."),
        howCalculated: t("Groups every matter by its current status and shows the proportion of each."),
        whyItMatters: t("Quickly see how much of your work is still pending versus completed."),
        type: "chart",
      },
      {
        name: t("Category breakdown"),
        explanation: t("Shows the types of matters you work on, such as Corporate, Litigation, Real Estate, etc."),
        howCalculated: t("Groups matters by their assigned category (practice area) and counts each."),
        whyItMatters: t("Understand your specialization and workload by area."),
        type: "chart",
      },
      {
        name: t("Jurisdiction"),
        explanation: t("Where your matters are filed - grouped by state, country, or region."),
        howCalculated: t("Extracts the jurisdiction from each matter and counts how many fall in each location."),
        whyItMatters: t("Shows your geographic coverage and filing patterns."),
        type: "chart",
      },
      {
        name: t("Overdue matters"),
        explanation: t("The number of matters past their due date that are still open."),
        howCalculated: t("Counts open matters where the deadline has passed and the matter isn't closed yet."),
        whyItMatters: t("Highlights work that needs urgent attention."),
        type: "card",
      },
      {
        name: t("Confidential matters"),
        explanation: t("How many of your matters are marked as confidential."),
        howCalculated: t("Counts matters with the confidential flag turned on."),
        whyItMatters: t("Keeps you aware of sensitive work in your portfolio."),
        type: "card",
      },
      {
        name: t("AI-processed matters"),
        explanation: t("Matters that have been analyzed or enriched by the AI engine."),
        howCalculated: t("Counts matters where the AI processing step has been completed."),
        whyItMatters: t("Shows how much of your work benefits from AI assistance."),
        type: "card",
      },
      {
        name: t("Average matter duration"),
        explanation: t("The typical time it takes you to close a matter, from creation to completion."),
        howCalculated: t("Measures the number of days between when a matter was created and when it was closed, then averages across all closed matters."),
        whyItMatters: t("Helps you benchmark your efficiency and set realistic timelines."),
        type: "card",
      },
    ],
  },
  {
    id: "tasks",
    title: t("Tasks"),
    icon: <PiCheckSquareOffsetLight className="w-4 h-4" />,
    description: t("Individual to-do items and action steps within your matters."),
    metrics: [
      {
        name: t("Total tasks"),
        explanation: t("The number of tasks assigned to you in the selected time period."),
        howCalculated: t("Counts all tasks created and assigned to you between the selected dates."),
        whyItMatters: t("Tracks your day-to-day actionable workload."),
        type: "card",
      },
      {
        name: t("Tasks over time"),
        explanation: t("A timeline showing when tasks were created for you."),
        howCalculated: t("Groups tasks by their creation date into time buckets."),
        whyItMatters: t("Reveals when you're being assigned the most work."),
        type: "chart",
      },
      {
        name: t("Overdue tasks"),
        explanation: t("The number of tasks past their due date that are still not completed."),
        howCalculated: t("Counts tasks where the deadline has passed but no completion date is recorded."),
        whyItMatters: t("Flags items that need immediate attention."),
        type: "card",
      },
      {
        name: t("Average turnaround time"),
        explanation: t("How long it typically takes you to complete a task after it's assigned."),
        howCalculated: t("Measures the days between task creation and completion, then averages across all completed tasks."),
        whyItMatters: t("Measures your responsiveness and helps set better deadlines."),
        type: "card",
      },
    ],
  },
  {
    id: "clients",
    title: t("Clients"),
    icon: <PiUsersLight className="w-4 h-4" />,
    description: t("Your client relationships - who you work with and how your client base grows."),
    metrics: [
      {
        name: t("Total clients"),
        explanation: t("The number of clients you manage or created in the selected time period."),
        howCalculated: t("Counts all clients linked to your account that were created within the date range."),
        whyItMatters: t("Shows the size and growth of your client portfolio."),
        type: "card",
      },
      {
        name: t("Clients over time"),
        explanation: t("A timeline showing when you added new clients."),
        howCalculated: t("Groups your new clients by the date they were created."),
        whyItMatters: t("Visualizes how your client base grows over time."),
        type: "chart",
      },
      {
        name: t("Average matters per client"),
        explanation: t("How many matters/cases you handle per client on average."),
        howCalculated: t("Divides the total number of your matters by the total number of your clients."),
        whyItMatters: t("Indicates the depth of your client relationships."),
        type: "card",
      },
    ],
  },
  {
    id: "research",
    title: t("Research"),
    icon: <PiMagnifyingGlassLight className="w-4 h-4" />,
    description: t("Your AI-powered legal research activity - queries, tokens used, and citation results."),
    metrics: [
      {
        name: t("Research queries"),
        explanation: t("The number of research questions you asked the AI research engine."),
        howCalculated: t("Counts every research query you submitted during the selected period."),
        whyItMatters: t("Tracks how actively you use the AI research tool."),
        type: "card",
      },
      {
        name: t("Tokens used"),
        explanation: t("A measure of how much AI processing power your research consumed."),
        howCalculated: t("Sums up the total tokens (units of text processed) across all your research queries."),
        whyItMatters: t("Helps you understand your AI resource usage."),
        type: "card",
      },
      {
        name: t("Citations found"),
        explanation: t("The total number of relevant legal citations the AI found for your queries."),
        howCalculated: t("Sums the citations returned across all your research results."),
        whyItMatters: t("Shows the quality and depth of your research output."),
        type: "card",
      },
      {
        name: t("Research over time"),
        explanation: t("A timeline of your research activity."),
        howCalculated: t("Groups your research queries by date to show usage patterns."),
        whyItMatters: t("Reveals your research habits and peak activity periods."),
        type: "chart",
      },
      {
        name: t("Average research response time"),
        explanation: t("How quickly the AI returns results for your queries."),
        howCalculated: t("Measures the seconds between when you submit a query and when results arrive, averaged across all queries."),
        whyItMatters: t("Helps set expectations for research turnaround."),
        type: "card",
      },
    ],
  },
  {
    id: "drafts",
    title: t("Drafts and insights"),
    icon: <PiFileTextLight className="w-4 h-4" />,
    description: t("Documents drafted and AI-generated insights created."),
    metrics: [
      {
        name: t("Drafts created"),
        explanation: t("The number of document drafts you created."),
        howCalculated: t("Counts all drafts linked to your account during the period."),
        whyItMatters: t("Tracks your document production."),
        type: "card",
      },
      {
        name: t("Insights generated"),
        explanation: t("AI-generated summaries and recommendations produced from your data."),
        howCalculated: t("Counts insight entries generated from your workflows."),
        whyItMatters: t("Shows how much AI-assisted analysis you're leveraging."),
        type: "card",
      },
    ],
  },
  {
    id: "files",
    title: t("Files"),
    icon: <PiFileTextLight className="w-4 h-4" />,
    description: t("Documents, images, and other files you've uploaded to the platform."),
    metrics: [
      {
        name: t("Files uploaded"),
        explanation: t("The total number of files you uploaded during the selected period."),
        howCalculated: t("Counts every file you uploaded between the start and end dates."),
        whyItMatters: t("Measures how actively you use the document management system."),
        type: "card",
      },
      {
        name: t("Storage used"),
        explanation: t("The total disk space your uploaded files occupy."),
        howCalculated: t("Sums the file sizes of all your uploads and displays it in a readable format (KB, MB, GB)."),
        whyItMatters: t("Helps you manage your storage quota."),
        type: "card",
      },
      {
        name: t("Files over time"),
        explanation: t("A timeline of your file uploads."),
        howCalculated: t("Groups uploads by date to show your uploading activity."),
        whyItMatters: t("Reveals your document management patterns."),
        type: "chart",
      },
    ],
  },
  {
    id: "templates",
    title: t("Templates"),
    icon: <PiArticleLight className="w-4 h-4" />,
    description: t("Reusable document templates you've created or managed."),
    metrics: [
      {
        name: t("Total templates"),
        explanation: t("The number of templates you own."),
        howCalculated: t("Counts all active templates linked to your account."),
        whyItMatters: t("Shows how much reusable content you've built."),
        type: "card",
      },
      {
        name: t("Templates over time"),
        explanation: t("When you created your templates."),
        howCalculated: t("Groups template creation dates into a timeline."),
        whyItMatters: t("Tracks your investment in workflow efficiency."),
        type: "chart",
      },
    ],
  },
  {
    id: "data-sources",
    title: t("Data sources"),
    icon: <PiDatabaseLight className="w-4 h-4" />,
    description: t("Knowledge bases and indexed document libraries you use for research."),
    metrics: [
      {
        name: t("Knowledge bases"),
        explanation: t("The number of data source collections you've set up."),
        howCalculated: t("Counts all data source entries you created."),
        whyItMatters: t("Shows the breadth of your knowledge resources."),
        type: "card",
      },
      {
        name: t("Indexed documents"),
        explanation: t("How many individual documents have been processed and indexed."),
        howCalculated: t("Sums the indexed document count across all your data sources."),
        whyItMatters: t("Indicates the depth of searchable content available to you."),
        type: "card",
      },
    ],
  },
  {
    id: "tables",
    title: t("Tables"),
    icon: <PiTableLight className="w-4 h-4" />,
    description: t("Structured data tables you've created for analysis."),
    metrics: [
      {
        name: t("Total tables"),
        explanation: t("The number of data tables you've created."),
        howCalculated: t("Counts all active tables linked to your account."),
        whyItMatters: t("Tracks your structured data management activity."),
        type: "card",
      },
      {
        name: t("Documents analyzed"),
        explanation: t("How many documents have been processed through your tables."),
        howCalculated: t("Sums the analyzed document count across all your tables."),
        whyItMatters: t("Shows how much structured analysis you've performed."),
        type: "card",
      },
    ],
  },
  {
    id: "personal-productivity",
    title: t("Your productivity"),
    icon: <PiTrendUpLight className="w-4 h-4" />,
    description: t("Personal performance metrics - track your efficiency, AI usage, workload balance, and research quality."),
    metrics: [
      {
        name: t("Task on-time rate"),
        explanation: t("A gauge showing what percentage of your tasks you completed before their deadline."),
        howCalculated: t("Of all your completed tasks that had a due date, measures how many were finished on or before that date."),
        whyItMatters: t("Helps you understand your reliability and time management. A high percentage shows strong deadline discipline."),
        type: "gauge",
      },
      {
        name: t("Your AI usage"),
        explanation: t("A gauge showing how much you leverage AI tools - both for processing matters and conducting research."),
        howCalculated: t("Combines two measures: the percentage of your matters that were AI-processed and a score based on your research query volume, then averages them."),
        whyItMatters: t("Shows whether you're taking full advantage of the AI tools available to you."),
        type: "gauge",
      },
      {
        name: t("Your productivity trend"),
        explanation: t("A line chart showing how many tasks you completed over time."),
        howCalculated: t("Groups your completed tasks by date into time buckets (days, weeks, or months)."),
        whyItMatters: t("Reveals whether your output is increasing, steady, or declining - and helps you spot your most productive periods."),
        type: "chart",
      },
      {
        name: t("Your research quality"),
        explanation: t("A line chart tracking the percentage of your research results that received positive feedback."),
        howCalculated: t("Looks at feedback you gave on research results ('thumbs up' vs others) and plots the positive rate over time."),
        whyItMatters: t("A rising trend means the research tool is getting you better results. A declining trend may suggest trying different search strategies."),
        type: "chart",
      },
      {
        name: t("Your matter stages"),
        explanation: t("A bar chart showing how your matters are distributed across pipeline stages."),
        howCalculated: t("Counts your matters in each stage (Intake, Review, Active, Closed, etc.) and displays them in order."),
        whyItMatters: t("Quickly see where your cases are piling up and what needs to move forward."),
        type: "chart",
      },
      {
        name: t("Your workload snapshot"),
        explanation: t("A bar chart showing your current open matters, pending tasks, and overdue tasks at a glance."),
        howCalculated: t("Counts open (not closed) matters, incomplete tasks, and tasks past their due date."),
        whyItMatters: t("A quick health check on your current workload. If overdue tasks are rising, it's time to reprioritize."),
        type: "chart",
      },
    ],
  },
];

const getExecutiveSections = (t: TFunction): HelpSection[] => [
  {
    id: "matter-pipeline",
    title: t("Matter pipeline and velocity"),
    icon: <PiChartBarLight className="w-4 h-4" />,
    description: t("Understand how matters flow through your organization's pipeline - from intake to closure."),
    metrics: [
      {
        name: t("Matter pipeline funnel"),
        explanation: t("A bar chart showing how many matters sit at each stage of your workflow (e.g., Intake, Review, Active, Closed)."),
        howCalculated: t("Counts all active matters in each stage, ordered by your workflow sequence. This gives you a snapshot of where work is piling up."),
        whyItMatters: t("Instantly spot bottlenecks. If too many matters are stuck in one stage, it signals a process issue that needs attention."),
        type: "chart",
      },
      {
        name: t("Matter stage velocity"),
        explanation: t("Shows the average number of days matters spend in each stage before moving to the next."),
        howCalculated: t("Looks at the history of stage changes for each matter and measures the time gap between entering and leaving each stage, then averages across all matters."),
        whyItMatters: t("Identifies slow stages in your pipeline. If matters spend weeks in 'Review' but only days in other stages, that's where to focus improvement efforts."),
        type: "chart",
      },
      {
        name: t("Priority vs. type matrix"),
        explanation: t("A grouped bar chart crossing matter priority levels (High, Medium, Low) with matter types (Litigation, Corporate, etc.)."),
        howCalculated: t("Groups all matters by their priority level and category, then counts each combination."),
        whyItMatters: t("Reveals whether high-priority work is concentrated in a specific practice area, helping you allocate resources more effectively."),
        type: "chart",
      },
    ],
  },
  {
    id: "team-workload",
    title: t("Team and workload"),
    icon: <PiUsersLight className="w-4 h-4" />,
    description: t("How work is distributed across your team - balance, productivity, and collaboration patterns."),
    metrics: [
      {
        name: t("Lawyer workload balance"),
        explanation: t("A bar chart showing how many matters and tasks each team member is handling."),
        howCalculated: t("For each active team member, counts their assigned matters and tasks, then displays them side by side."),
        whyItMatters: t("Prevents burnout by showing if work is evenly distributed or if some people are overloaded while others have capacity."),
        type: "chart",
      },
      {
        name: t("Team productivity score"),
        explanation: t("A radar chart comparing each team member's task completion performance."),
        howCalculated: t("Counts the number of tasks each active team member completed within the selected period and plots them on a radar chart."),
        whyItMatters: t("Gives a visual comparison of individual productivity, helping identify top performers and those who may need support."),
        type: "chart",
      },
      {
        name: t("Department comparison"),
        explanation: t("A bar chart comparing departments by team size."),
        howCalculated: t("Counts how many active members each department has."),
        whyItMatters: t("Provides a quick overview of organizational structure and resource distribution across departments."),
        type: "chart",
      },
      {
        name: t("Collaboration intensity"),
        explanation: t("A line chart tracking message volume in your team chat over time."),
        howCalculated: t("Counts chat messages sent each day/week/month across all organization channels."),
        whyItMatters: t("High collaboration often correlates with better outcomes. Tracking this helps you understand team communication health."),
        type: "chart",
      },
    ],
  },
  {
    id: "clients-executive",
    title: t("Client portfolio"),
    icon: <PiUsersLight className="w-4 h-4" />,
    description: t("Track how your client base grows and how well your team serves them."),
    metrics: [
      {
        name: t("Client portfolio growth"),
        explanation: t("A line chart showing new client additions and your cumulative client count over time."),
        howCalculated: t("Plots two lines: (1) how many new clients were added each period, and (2) the running total of all clients up to that point."),
        whyItMatters: t("Shows whether your client base is growing, plateauing, or declining - a key indicator of business health."),
        type: "chart",
      },
      {
        name: t("Client onboarding funnel"),
        explanation: t("A bar chart showing Total Clients, then Onboarded, then Active, like a conversion funnel."),
        howCalculated: t("Counts how many clients exist, how many have completed onboarding, and how many are actively engaged."),
        whyItMatters: t("If many clients are created but few become active, your onboarding process may need improvement."),
        type: "chart",
      },
      {
        name: t("Clients per lawyer"),
        explanation: t("The average number of active clients per active team member."),
        howCalculated: t("Divides total active clients by total active team members."),
        whyItMatters: t("A high ratio may indicate stretched capacity; a low ratio might suggest room for growth."),
        type: "card",
      },
      {
        name: t("Top clients by activity"),
        explanation: t("A bar chart of your most active clients ranked by the total number of matters and tasks they're involved in."),
        howCalculated: t("Sums each client's matters and tasks, then ranks the top 10."),
        whyItMatters: t("Highlights your most engaged clients - great for relationship management and prioritization."),
        type: "chart",
      },
    ],
  },
  {
    id: "ai-research",
    title: t("AI and research intelligence"),
    icon: <PiLightningLight className="w-4 h-4" />,
    description: t("Measure how effectively your organization leverages AI tools and research capabilities."),
    metrics: [
      {
        name: t("AI utilization rate"),
        explanation: t("A gauge showing what percentage of your organization's work involves AI assistance."),
        howCalculated: t("Combines two measures: (1) the percentage of matters processed by AI, and (2) the percentage of team members who use the research tool. The gauge shows the average of both."),
        whyItMatters: t("Low utilization suggests the team may not be taking full advantage of AI tools - an opportunity for training or process improvement."),
        type: "gauge",
      },
      {
        name: t("Tokens per citation"),
        explanation: t("The average amount of AI processing needed to find one useful citation."),
        howCalculated: t("Divides total tokens consumed by total citations found across all research queries."),
        whyItMatters: t("Lower is better - fewer tokens per citation means more efficient research. A rising number could indicate the need to refine search strategies."),
        type: "card",
      },
      {
        name: t("Research quality trend"),
        explanation: t("A line chart showing the percentage of research results that received positive feedback over time."),
        howCalculated: t("Tracks the ratio of 'thumbs up' feedback to total feedback for research results, plotted over time."),
        whyItMatters: t("Rising quality means the AI is delivering more useful results. Declining quality may indicate changes in query patterns or data quality."),
        type: "chart",
      },
    ],
  },
  {
    id: "platform-health",
    title: t("Platform and compliance"),
    icon: <PiShieldCheckLight className="w-4 h-4" />,
    description: t("Monitor platform health, user adoption, deadlines, and license usage."),
    metrics: [
      {
        name: t("User adoption curve"),
        explanation: t("A line chart tracking new signups alongside active users over time."),
        howCalculated: t("Shows two lines: (1) new user registrations per period, and (2) users who logged in during that same period."),
        whyItMatters: t("Healthy adoption means new users are joining and existing users keep coming back. A gap between signups and active users may signal onboarding issues."),
        type: "chart",
      },
      {
        name: t("Session devices"),
        explanation: t("A doughnut chart showing what devices your team uses - Desktop, Mobile, Tablet, etc."),
        howCalculated: t("Looks at all user sessions in the period and groups them by reported device type."),
        whyItMatters: t("Helps you understand how your team accesses the platform and whether mobile optimization matters."),
        type: "chart",
      },
      {
        name: t("Error severity"),
        explanation: t("A doughnut chart breaking down platform errors by severity - Critical, High, Medium, Low."),
        howCalculated: t("Counts reported errors and groups them by severity level."),
        whyItMatters: t("A high proportion of critical errors needs immediate attention. Tracking this helps prioritize bug fixes and platform stability."),
        type: "chart",
      },
      {
        name: t("Task on-time trend"),
        explanation: t("A line chart showing what percentage of tasks were completed before their deadline over time."),
        howCalculated: t("For each period, checks how many completed tasks met their due date versus how many didn't, then plots the on-time percentage."),
        whyItMatters: t("Reveals whether your team consistently meets deadlines or if there's a pattern of delays that needs addressing."),
        type: "chart",
      },
      {
        name: t("Knowledge base freshness"),
        explanation: t("A gauge showing what percentage of your data sources have been updated recently."),
        howCalculated: t("Checks which data sources were indexed within the last 30 days. Sources older than 30 days are considered 'stale'."),
        whyItMatters: t("Stale knowledge bases may produce outdated research results. Keeping data fresh ensures better AI accuracy."),
        type: "gauge",
      },
      {
        name: t("Stale data sources"),
        explanation: t("The number of data sources that haven't been updated in over 30 days."),
        howCalculated: t("Counts data sources where the last indexing date is more than 30 days ago (or never indexed)."),
        whyItMatters: t("These specific sources should be reviewed and re-indexed to maintain research quality."),
        type: "card",
      },
      {
        name: t("License utilization"),
        explanation: t("A gauge showing what percentage of your purchased license seats are being used."),
        howCalculated: t("Divides the number of assigned seats by the total seats in your license plan."),
        whyItMatters: t("Low utilization means you're paying for unused seats. High utilization means you may need to plan for expansion."),
        type: "gauge",
      },
      {
        name: t("Seats used"),
        explanation: t("Shows exactly how many license seats are assigned out of your total."),
        howCalculated: t("Counts active seat assignments and displays them as 'X / Y' where X is used and Y is total."),
        whyItMatters: t("A simple way to check if you need more (or fewer) seats in your plan."),
        type: "card",
      },
    ],
  },
];

const getOrgExtraSections = (t: TFunction): HelpSection[] => [
  {
    id: "people",
    title: t("People and users"),
    icon: <PiUsersLight className="w-4 h-4" />,
    description: t("Your team members - who's active, how they're engaging, and session activity."),
    metrics: [
      {
        name: t("Team members"),
        explanation: t("The total number of people in your organization."),
        howCalculated: t("Counts all users associated with your organization."),
        whyItMatters: t("A baseline measure of your team size."),
        type: "card",
      },
      {
        name: t("Active members"),
        explanation: t("How many team members are currently active."),
        howCalculated: t("Counts users who are marked as active and haven't been deactivated."),
        whyItMatters: t("Shows your actual working headcount."),
        type: "card",
      },
      {
        name: t("Active sessions (24h)"),
        explanation: t("How many unique user sessions are active right now (within the last 24 hours)."),
        howCalculated: t("Counts sessions with activity in the last 24 hours."),
        whyItMatters: t("A real-time indicator of how many people are using the platform."),
        type: "card",
      },
    ],
  },
  {
    id: "chat",
    title: t("Chat and messaging"),
    icon: <PiChatsCircleLight className="w-4 h-4" />,
    description: t("Organization-wide chat and messaging activity."),
    metrics: [
      {
        name: t("Chats"),
        explanation: t("The total number of chat conversations in your organization."),
        howCalculated: t("Counts all active chat channels and direct messages."),
        whyItMatters: t("Shows how much communication is happening on the platform."),
        type: "card",
      },
      {
        name: t("Messages"),
        explanation: t("The total number of messages sent across all chats."),
        howCalculated: t("Counts every message sent during the selected time period."),
        whyItMatters: t("A direct measure of team communication volume."),
        type: "card",
      },
      {
        name: t("Messages over time"),
        explanation: t("A timeline of messaging activity."),
        howCalculated: t("Groups messages by date to show communication patterns."),
        whyItMatters: t("Reveals peak communication times and long-term trends."),
        type: "chart",
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Collapsible Section Component                                      */
/* ------------------------------------------------------------------ */

const CollapsibleSection: React.FC<{
  section: HelpSection;
  defaultOpen?: boolean;
}> = ({ section, defaultOpen = false }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="glass rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-start"
      >
        <span className="text-gray-700 dark:text-gray-300">{section.icon}</span>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{section.title}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{section.description}</p>
        </div>
        {isOpen ? (
          <PiCaretDownLight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <PiCaretRightLight className="w-4 h-4 text-gray-400 flex-shrink-0 rtl:-scale-x-100" />
        )}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-2 space-y-5 border-t border-gray-100 dark:border-gray-800">
          {section.metrics.map((metric, idx) => (
            <div key={idx} className="ps-4 border-s-2 border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 mb-2">
                <h5 className="text-sm font-medium text-gray-800 dark:text-gray-200">{metric.name}</h5>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                  {metric.type === "card" ? t("Number") : metric.type === "gauge" ? t("Gauge") : t("Chart")}
                </span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{metric.explanation}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-surface-sunken dark:bg-gray-800/60 rounded-lg px-4 py-3">
                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">{t("How it's calculated")}</span>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{metric.howCalculated}</p>
                </div>
                <div className="bg-surface-sunken dark:bg-gray-800/60 rounded-lg px-4 py-3">
                  <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">{t('Why it matters')}</span>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{metric.whyItMatters}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Suggestions Panel                                                  */
/* ------------------------------------------------------------------ */

const SuggestionsPanel: React.FC = () => {
  const { t } = useTranslation();
  const [suggestion, setSuggestion] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!suggestion.trim()) return;
    setSubmitting(true);
    try {
      await apis.post("/analytics/suggestion", { suggestion: suggestion.trim() });
      setSubmitted(true);
      setSuggestion("");
    } catch {
      // Still show success to the user
      setSubmitted(true);
      setSuggestion("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-8 py-6">
      <div className="max-w-xl mx-auto space-y-6">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">{t("Suggest a new analytic")}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            {t("Is there something you'd like to see on your analytics dashboard that isn't there yet? Let us know and we'll consider adding it.")}
          </p>
        </div>

        {submitted ? (
          <div className="glass rounded-lg p-6 text-center space-y-3">
            <PiChatDotsLight className="w-8 h-8 text-gray-400 mx-auto" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t("Thank you for your feedback")}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("We review every suggestion and will consider it for upcoming updates.")}</p>
            </div>
            <button
              onClick={() => setSubmitted(false)}
              className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline transition-colors"
            >
              {t("Submit another suggestion")}
            </button>
          </div>
        ) : (
          <div className="glass rounded-lg p-5 space-y-4">
            <Textarea
              id="analytics-suggestion"
              label={t("What would you like to track?")}
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              placeholder={t("For example: I'd like to see how many matters each team member closes per month, or a chart comparing response times across different practice areas...")}
              h="h-32"
              className="resize-none"
            />
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-gray-400">{t("Your suggestion helps us improve the platform for everyone")}</p>
              <Button
                variant="primary"
                size="small"
                onClick={handleSubmit}
                disabled={!suggestion.trim() || submitting}
                isLoading={submitting}
                icon={<PiPaperPlaneRightLight className="w-3.5 h-3.5" />}
              >
                {submitting ? t("Sending...") : t("Submit")}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">{t("Examples of things you can ask for")}</h4>
          {[
            "A comparison of case outcomes across different practice areas",
            "Revenue trend by client over the last 12 months",
            "Average time to first response on new matters",
            "Team utilization rate based on hours logged vs capacity",
          ].map((example, i) => (
            <button
              key={i}
              onClick={() => setSuggestion(example)}
              className="glass w-full text-start px-4 py-3 text-xs text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              {t(example)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Modal                                                         */
/* ------------------------------------------------------------------ */

type ModalTab = "personal" | "organization" | "suggestions";

const AnalyticsHelpModal: React.FC<AnalyticsHelpModalProps> = ({ isOpen, onClose, isOrgAdmin }) => {
  const { t } = useTranslation();
  const [activeHelp, setActiveHelp] = useState<ModalTab>("personal");

  if (!isOpen) return null;

  const personalSections = getPersonalSections(t);
  const orgSections = [...getPersonalSections(t), ...getOrgExtraSections(t), ...getExecutiveSections(t)];

  const sections = activeHelp === "personal" ? personalSections : activeHelp === "organization" ? orgSections : [];

  const totalMetrics = sections.reduce((sum, s) => sum + s.metrics.length, 0);

  const leftTabs: { value: ModalTab; label: string; icon: React.ReactNode }[] = [
    { value: "personal", label: "Personal", icon: <PiUserLight className="w-3.5 h-3.5" /> },
    ...(isOrgAdmin ? [{ value: "organization" as ModalTab, label: "Organization", icon: <PiBuildingsLight className="w-3.5 h-3.5" /> }] : []),
  ];

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink/20 backdrop-blur-[2px]" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-5xl max-h-[90vh] bg-surface-sunken dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-3">
            <PiQuestionLight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t("Understanding your analytics")}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {activeHelp === "suggestions"
                  ? t("Tell us what analytics you'd like to see")
                  : `${t("Learn what every metric means and how it's calculated")} - ${totalMetrics} ${t("metrics")}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <PiXLight className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex items-center justify-between px-8 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-2">
            {leftTabs.map((tab) => {
              const isActive = activeHelp === tab.value;
              return (
                <button
                  key={tab.value}
                  onClick={() => setActiveHelp(tab.value)}
                  className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded nav-pill ${
                    isActive
                      ? "nav-pill-active text-primary-600 dark:text-primary-300"
                      : "text-gray-600 dark:text-gray-300"
                  }`}
                >
                  {tab.icon}
                  {t(tab.label)}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveHelp("suggestions")}
              className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded nav-pill ${
                activeHelp === "suggestions"
                  ? "nav-pill-active text-primary-600 dark:text-primary-300"
                  : "text-gray-600 dark:text-gray-300"
              }`}
            >
              <PiChatDotsLight className="w-3.5 h-3.5" />
              {t("Suggestions?")}
            </button>
          </div>
        </div>

        {/* Content */}
        {activeHelp === "suggestions" ? (
          <SuggestionsPanel />
        ) : (
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-3">
            {/* Legend */}
            <div className="flex items-center gap-4 mb-2">
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
                <span className="text-[10px] text-gray-500 dark:text-gray-400">{t("Numbers")}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
                <span className="text-[10px] text-gray-500 dark:text-gray-400">{t("Charts")}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
                <span className="text-[10px] text-gray-500 dark:text-gray-400">{t("Gauges")}</span>
              </div>
            </div>

            {sections.map((section) => (
              <CollapsibleSection key={section.id} section={section} />
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <p className="text-[10px] text-gray-400 dark:text-gray-500">
            {t("All data is calculated from the time range you've selected. Adjusting the date filter will update every metric.")}
          </p>
          <Button variant="neutral" size="small" onClick={onClose}>
            {t("Close")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsHelpModal;
